import { useQuery } from '@tanstack/react-query';
import { httpClient } from '../api/http';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setRoles } from '../store/slices/utils';
import { AdminGrade, NormalizedRole, userRolesPropsTypes } from '../types';
import { RootState } from '../store';
import { Alert } from '@piggytechng/component-library';
import { capitalize } from '../helpers/utils';
import { ACTION_ALLOWED_ROLE_IDS, ACTION_MINIMUM_GRADE, DEFAULT_ADMIN_GRADE, GRADE_1_ROLE_IDS } from '../constants';

const useRolePermissions = ({ checkCustomer = false }: userRolesPropsTypes) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const systemsRoleAndPermissions = useSelector((state: RootState) => state.rolesAndPermissions);

  const {
    data: rolesAndPermissions,
    isLoading: isLoadingRolesAndPermissions,
    // refetch: refetchPiggybankBalanceQ,
  } = useQuery({
    queryKey: ['roles-permissions'],
    queryFn: () => httpClient.get(`admin/management/roles-permissions`).then((res) => res.data.data),
  });

  const roles = useMemo(() => {
    if (!rolesAndPermissions?.data) return [];

    const res = rolesAndPermissions.data.map((role: any) => {
      return { label: capitalize(role.roleName), value: role.roleId };
    });
    return res;
  }, [rolesAndPermissions]);

  const [AdminParams, setAdminParams] = useState<{ cursor: string; limit: number; filter: string; value: string }>({
    cursor: '',
    limit: 10,
    filter: '',
    value: '',
  });

  const canResetSearch = useMemo(() => {
    return AdminParams.filter || AdminParams.value;
  }, [AdminParams]);

  const [nextCursor, setNextCursor] = useState<any>('');
  const [isLoading, setIsLoading] = useState({
    isLoadingMoreAdmins: false,
    isLoadingAdmins: false,
    isInvitingAdmin: false,
    isRemovingAdmin: false,
    isEditingAdmin: false,
  });

  const [AllAdmins, setAllAdmins] = useState<any[]>([]);
  const fetchAllAdmins = async (
    options?: {
      isLoadingMoreAdmins: boolean;
      cursor?: string;
      value?: string;
      filter?: string;
    },
    reset = false,
  ) => {
    try {
      setIsLoading((prev) => ({
        ...prev,
        isLoadingMoreAdmins: options?.isLoadingMoreAdmins || false,
        isLoadingAdmins: !options?.isLoadingMoreAdmins,
      }));

      if (reset) {
        setAdminParams({
          cursor: '',
          limit: 10,
          filter: '',
          value: '',
        });
      }

      const query = reset
        ? `limit=${AdminParams.limit}`
        : `${AdminParams.cursor || options?.cursor ? `cursor=${options?.cursor ?? AdminParams.cursor}&` : ''}${
            AdminParams.filter || options?.filter
              ? `filter=${options?.filter ?? AdminParams.filter}${
                  AdminParams.value || options?.value ? `&value=${options?.value ?? AdminParams.value}` : ''
                }`
              : ''
          }&limit=${AdminParams.limit}`;
      const response = await httpClient.get(`admin/management/all-admins?${query}`);
      if (response.data?.status === true) {
        const data = response.data.data.data;
        if (options?.isLoadingMoreAdmins) {
          const updatedAdmins = AllAdmins.concat(data.admins || []);
          setAllAdmins(updatedAdmins);
        } else {
          setAllAdmins(data.admins || []);
        }

        if (data?.cursorEnd) {
          setNextCursor(data.cursorEnd);
        } else {
          setNextCursor(null);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, isLoadingMoreAdmins: false, isLoadingAdmins: false }));
    }
  };

  function normalizeRoles(rawData: any) {
    const result: Record<string, NormalizedRole> = {};

    if (!rawData?.data) return result;

    for (const role of rawData.data) {
      const { roleId, roleName, permissions } = role;

      result[roleId] = {
        roleName,
        permissions: {},
      };

      for (const [category, perms] of Object.entries(permissions)) {
        if (Array.isArray(perms)) {
          result[roleId].permissions[category] = perms.reduce((acc: Record<string, boolean>, perm: string) => {
            acc[perm] = true;
            return acc;
          }, {});
        } else {
          result[roleId].permissions[category] = {};
        }
      }
    }

    dispatch(setRoles(result));

    return result;
  }

  useEffect(() => {
    if (!isLoadingRolesAndPermissions) {
      normalizeRoles(rolesAndPermissions);
    }
  }, [isLoadingRolesAndPermissions]);

  const usersRoleId: string = localStorage.getItem('userRoleID') ?? '';

  const roleDetails: any = systemsRoleAndPermissions.roles[usersRoleId as keyof typeof systemsRoleAndPermissions.roles];
  const isSuperAdmin = usersRoleId === '5a90f7ac-4a64-47c3-8a47-9b02174b789a';

  useEffect(() => {
    if (Object.keys(systemsRoleAndPermissions.roles).length || !isLoadingRolesAndPermissions) {
      if (!roleDetails) return;

      if (checkCustomer) {
        if (roleDetails?.permissions?.customerPage?.view === undefined) {
          Alert.warn('You do not have access to view this page');
          navigate('/customers');
        }
      }
    }
  }, [isLoadingRolesAndPermissions, systemsRoleAndPermissions, roleDetails]);

  const adminGrade: AdminGrade = useMemo(
    () => (GRADE_1_ROLE_IDS.includes(usersRoleId) ? 1 : DEFAULT_ADMIN_GRADE),
    [usersRoleId],
  );

  const canPerformAction = (actionType: string, action: string) => {
    const key = `${actionType}.${action}`;

    const allowedRoleIds = ACTION_ALLOWED_ROLE_IDS[key];
    if (allowedRoleIds) return allowedRoleIds.includes(usersRoleId);

    if (isSuperAdmin) return true;

    const minimumGrade = ACTION_MINIMUM_GRADE[key];
    return !minimumGrade || adminGrade >= minimumGrade;
  };

  const restrictActionByRole = (actionType: string, action: string) => {
    if (!canPerformAction(actionType, action)) {
      Alert.warn('You do not have access to execute this action');
      return true;
    }

    return false;
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const restrictTabViewByRole = (actionType: string, action: string, returnAction?: () => void) => {
    if (isSuperAdmin) return;

    if (roleDetails?.permissions?.[actionType]?.[action] === undefined) {
      // returnAction?.();
      // Alert.warn('You do not have access to view this page');
      // navigate('/default/view');
    }
  };

  const inviteAdmin = async (adminData: any) => {
    try {
      setIsLoading((prev) => ({ ...prev, isInvitingAdmin: true }));
      const res = await httpClient.post(`admin/management/invite-admin`, adminData);
      Alert.success('Admin invited successfully');
      await fetchAllAdmins();
      return res;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, isInvitingAdmin: false }));
    }
  };

  const editAdmin = async (payload: any, callback?: () => void) => {
    try {
      setIsLoading((prev) => ({ ...prev, isEditingAdmin: true }));
      const res = await httpClient.post(`admin/management/assign-role`, payload);
      Alert.success('Admin updated successfully');
      await fetchAllAdmins();
      if (callback) callback();
      return res;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, isEditingAdmin: false }));
    }
  };

  const removeAdmin = async (payload: any) => {
    try {
      setIsLoading((prev) => ({ ...prev, isRemovingAdmin: true }));
      const res = await httpClient.post(`admin/management/update-admin-access`, payload);
      Alert.success('Admin removed successfully');
      fetchAllAdmins();
      return res;
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading((prev) => ({ ...prev, isRemovingAdmin: false }));
    }
  };

  return {
    rolesAndPermissions,
    isLoadingRolesAndPermissions,
    isSuperAdmin,
    adminGrade,
    canPerformAction,
    restrictActionByRole,
    restrictTabViewByRole,
    AllAdmins,
    isLoading,
    fetchAllAdmins,
    inviteAdmin,
    removeAdmin,
    editAdmin,
    setNextCursor,

    roles,
    nextCursor,
    setAdminParams,
    AdminParams,
    canResetSearch,
  };
};

export default useRolePermissions;
